import { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { handleError } from 'services/Error';
import { useAppSelector } from 'store';
import { useGetLostReferralsQuery } from 'store/api/auctionApi';
import { useGetReferralQuery } from 'store/api/performanceApi';
import { useLiveReferralData } from 'store/liveReferrals/liveReferralsHooks';

export const AppRoutes = {
    callback: '/callback',
    login: '/login',
    loggedOut: '/logged-out',
    home: '/home',
    worklist: '/worklist',
    /** Generic referral link that will reroute to Live, won, or lost referral details page */
    referralDetails: (referralId = ':referralId') => `/referrals/${referralId}`,
    liveReferrals: '/live-referrals',
    liveReferralDetails: (referralId = ':referralId') => `/live-referrals/${referralId}`,
    /** Page does not actually exist, so will reroute to home */
    manageReferrals: '/manage-referrals',
    wonReferrals: '/manage-referrals/won',
    upcomingReferrals: '/manage-referrals/upcoming',
    wonReferralDetails: (referralId = ':referralId') => `/manage-referrals/won/${referralId}`,
    claimSubmissionForm: (referralId = ':referralId') =>
        `/manage-referrals/won/${referralId}/submit-claim`,
    expiredReferrals: '/manage-referrals/expired',
    declinedReferrals: '/manage-referrals/declined',
    lostReferrals: '/manage-referrals/lost',
    claimsHistory: '/manage-referrals/claims',
    claimDetails: (transactionId = ':transactionId') => `/manage-referrals/claims/${transactionId}`,
    /** Page does not actually exist, so will reroute to home */
    admin: '/admin',
    automations: '/admin/automations',
    automationDetails: (ruleId = ':ruleId') => `/admin/automations/${ruleId}`,
    prescriberLists: '/admin/prescriber-lists',
    prescriberListDetails: (prescriberListId = ':prescriberListId') =>
        `/admin/prescriber-lists/${prescriberListId}`,
    stateLists: '/admin/state-lists',
    stateListDetails: (stateListId = ':stateListId') => `/admin/state-lists/${stateListId}`,
    termsAndConditions: '/terms-and-conditions',
    termsOfUse: '/terms-of-use',
    privacyPolicy: '/privacy-policy',
    healthPlanNotes: '/health-plan-notes',
    updatesHistory: '/updates-history',
    allMessages: '/messages',
    conversationDetails: (displayId?: string) =>
        `/messages/${displayId ? encodeURIComponent(displayId) : ':displayId'}`,
} as const;

export interface BackToDestination {
    /** The route for the return destination */
    route: string;
    /** The display name for the return destination */
    text: string;
}

/**
 * For a page that shows referral details, will determine if the current route is valid and
 * will reroute if necessary.
 */
export const useCurrentReferralLocation = (defaultRoute: string = AppRoutes.home) => {
    const { referralId } = useParams() as { referralId: string };
    const location = useLocation();
    const navigate = useNavigate();

    const { liveReferralData, liveReferralDataLoaded } = useLiveReferralData();
    const liveReferral = liveReferralData.find((referral) => referral.referralId === referralId);

    const pendingReferralData = useAppSelector(
        (state) => state.wonReferralList.pendingReferralData,
    );
    const pendingReferral = pendingReferralData?.find(
        (referral) => referral.referralId === referralId,
    );

    const { data: wonReferral, isLoading: wonReferralLoading } = useGetReferralQuery(referralId);

    const { data: lostReferrals, isLoading: lostReferralsLoading } = useGetLostReferralsQuery();
    const lostReferral = lostReferrals?.find((referral) => referral.referralId === referralId);

    const allDataLoaded =
        liveReferralDataLoaded &&
        !!pendingReferralData &&
        !wonReferralLoading &&
        !lostReferralsLoading;

    useEffect(() => {
        const path = (() => {
            if (liveReferral) {
                return AppRoutes.liveReferralDetails(referralId);
            }
            if (pendingReferral ?? wonReferral) {
                return AppRoutes.wonReferralDetails(referralId);
            }
            if (lostReferral) {
                return AppRoutes.lostReferrals;
            }
            return undefined;
        })();

        if (path && !location.pathname.startsWith(path, 0)) {
            // If there's a referral match, and the current path doesn't contain the match, navigate to the correct path.
            navigate({ pathname: path, hash: location.hash }, { replace: true });
        } else if (!path && allDataLoaded) {
            // If all data is loaded and there's no match, go to the default route.
            handleError('Unable to find referral.');
            navigate(defaultRoute, { replace: true });
        }
        // If there's a referral match, and the current path contains the match, do nothing.
    }, [
        liveReferral,
        pendingReferral,
        wonReferral,
        lostReferral,
        allDataLoaded,
        referralId,
        navigate,
        location,
        defaultRoute,
    ]);
};
